import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2,  Loader2, Send, MessageCircle, Copy } from "lucide-react";
import clsx from "clsx";
import { toast } from "./toaster";

function isTelegramWebApp() {
  // @ts-ignore
  return typeof window !== "undefined" && !!(window as any).Telegram?.WebApp;
}

type Props = {
  url: string;
  title?: string;
  text?: string;
  previewSrc?: string;         // ← превью
  compact?: boolean;
  className?: string;
  vibrateTick?: () => void;
  vibrateSuccess?: () => void;
  bottomOffset?: number;
};

export default function ShareButton({
  url, title, text, previewSrc,
  compact = false, className,
  vibrateTick, vibrateSuccess,
  bottomOffset = 72,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  async function doShare() {
    if (busy) return;
    vibrateTick?.();
    setBusy(true);
    try {
      if (!isTelegramWebApp() && navigator.share) {
        await navigator.share({ url, title, text });
        vibrateSuccess?.();
        toast("Ссылка отправлена", "success");
      } else {
        setOpen(true);
      }
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  }

  const btnClass = clsx(
    "relative inline-flex h-9 w-9 items-center justify-center rounded-full",
    "border border-white/15 bg-white/5 hover:bg-white/10 transition",
    "focus:outline-none focus:ring-2 focus:ring-pink-400/60",
    "disabled:opacity-60 disabled:cursor-not-allowed",
    className
  );

  const sheetPadding = { paddingBottom: `calc(env(safe-area-inset-bottom) + ${bottomOffset}px)` };

  if (compact) {
    return (
      <>
        <button type="button" aria-label="Поделиться" onClick={doShare} disabled={busy} className={btnClass}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-white/80" /> : <Share2 className="h-4 w-4 text-white/85" />}
        </button>

        {/* Bottom Sheet */}
        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed inset-0 z-[70]"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            >
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <motion.div
                className="absolute left-0 right-0 bottom-0 rounded-t-2xl bg-zinc-900 border-t border-white/10 p-4"
                initial={{ y: 420 }} animate={{ y: 0 }} exit={{ y: 420 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
                onClick={(e) => e.stopPropagation()}
                style={sheetPadding}                   // ← подняли над нижним баром
              >
                <div className="mx-auto h-1.5 w-10 rounded-full bg-white/15 mb-3" />

                {/* превью */}
                {(previewSrc || title || text) && (
                  <div className="flex gap-3 mb-3 items-center">
                    <div className="h-16 w-16 rounded-xl overflow-hidden border border-white/10 bg-white/5 flex-shrink-0">
                      {previewSrc ? (
                        <img src={previewSrc} alt={title ?? "preview"} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full" />
                      )}
                    </div>
                    <div className="min-w-0">
                      {title && <div className="text-white/90 text-sm truncate">{title}</div>}
                      {text && <div className="text-white/60 text-xs line-clamp-2">{text}</div>}
                      <div className="text-white/40 text-[11px] truncate mt-1">{url}</div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2 mb-3">
                  <ActionChip onClick={() => openTelegram(url)} label="Telegram" Icon={Send} />
                  <ActionChip onClick={() => openWhatsApp(url)} label="WhatsApp" Icon={MessageCircle} />
                  <ActionChip onClick={() => copy(url, vibrateSuccess)} label="Скопировать" Icon={Copy} />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => window.open(url, "_blank")}
                    className="flex-1 h-10 rounded-xl bg-white/10 hover:bg-white/15 text-white/90 border border-white/10"
                  >
                    Открыть ссылку
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    className="h-10 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 border border-white/10"
                  >
                    Закрыть
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // некомпактный вариант
  return (
    <button onClick={doShare} disabled={busy}
      className={clsx(
        "inline-flex items-center gap-2 rounded-xl px-3 h-9",
        "border border-white/10 bg-white/5 hover:bg-white/10 transition text-sm text-white/85",
        "focus:outline-none focus:ring-2 focus:ring-pink-400/60", className
      )}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin text-white/80" /> : <Share2 className="h-4 w-4" />}
      <span>Поделиться</span>
    </button>
  );
}

function ActionChip({ onClick, label, Icon }:{
  onClick: () => void; label: string; Icon: React.ComponentType<{className?:string}>;
}) {
  return (
    <button onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 rounded-xl h-20 bg-white/5 hover:bg-white/10 border border-white/10 text-white/85">
      <Icon className="h-5 w-5" />
      <span className="text-xs">{label}</span>
    </button>
  );
}

function copy(url: string, vibrateSuccess?: () => void) {
  (navigator.clipboard?.writeText(url) ?? Promise.reject()).then(() => {
    vibrateSuccess?.();
    toast("Ссылка скопирована", "success");
  }).catch(() => {
    const el = document.createElement("textarea");
    el.value = url;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    el.remove();
    vibrateSuccess?.();
    toast("Ссылка скопирована", "success");
  });
}

function openTelegram(url: string) {
  const tg = `https://t.me/share/url?url=${encodeURIComponent(url)}`;
  window.open(tg, "_blank");
}
function openWhatsApp(url: string) {
  const wa = `https://wa.me/?text=${encodeURIComponent(url)}`;
  window.open(wa, "_blank");
}
